import type { QueryClient } from '@tanstack/react-query';

import { savePreferencesForScope } from '../api/user-preferences-api';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';

type AllPrefs = Record<PreferenceScope, Partial<UserPreferences>>;

// Per-scope queue shared by every pageOrder writer (drag save, reset, delete
// cleanup) so their HTTP requests can't land out-of-order on the backend.
// Lives at module scope so it survives hook remounts and sibling instances.
const queueByScope = new Map<PreferenceScope, Promise<unknown>>();

const EMPTY_ALL_PREFS: AllPrefs = {
  [PreferenceScope.GLOBAL]: {},
  [PreferenceScope.INDIVIDUAL]: {},
  [PreferenceScope.COMPANY]: {},
  [PreferenceScope.INDUSTRY]: {},
  [PreferenceScope.GEOGRAPHY]: {},
  [PreferenceScope.ROLE]: {},
  [PreferenceScope.SKILL]: {},
  [PreferenceScope.ACTIVITY]: {},
};

/**
 * Synchronously update the React Query cache with the new pageOrder for
 * `scope`. Every pageOrder writer must call this before `enqueuePageOrderSave`
 * so siblings reading the cache (e.g. delete cleanup) see the latest logical
 * value, not a snapshot that predates an in-flight reset or drag save.
 * Returns the previous AllPrefs for rollback callers.
 */
export function writePageOrderToCache(
  queryClient: QueryClient,
  scope: PreferenceScope,
  ids: string[]
): AllPrefs | undefined {
  // Cancel any in-flight initial fetch so it can't land after this write.
  void queryClient.cancelQueries({ queryKey: ['user-preferences'] });
  const previous = queryClient.getQueryData<AllPrefs>(['user-preferences']);
  const base = previous ?? EMPTY_ALL_PREFS;
  queryClient.setQueryData<AllPrefs>(['user-preferences'], {
    ...base,
    [scope]: {
      ...base[scope],
      [UserPreferenceKey.PageOrder]: ids,
    },
  });
  return previous;
}

/**
 * Serialize a pageOrder write for `scope` so HTTP requests can't land
 * out-of-order on the backend. Errors from earlier writes are swallowed
 * so the next write still fires.
 */
export function enqueuePageOrderSave(
  scope: PreferenceScope,
  ids: string[]
): Promise<unknown> {
  const previous = queueByScope.get(scope) ?? Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(() =>
      savePreferencesForScope(scope, {
        [UserPreferenceKey.PageOrder]: ids,
      })
    );
  queueByScope.set(scope, next);
  return next;
}
